/**
 * Blog post loader — imports all markdown files from /content/blog/
 * at build time using Vite's import.meta.glob.
 *
 * This is the core engine: scheduled tasks write .md files,
 * Vite picks them up at build, and this module serves them to React.
 */

import type { BlogPost, BlogFAQ, BlogCategory, BlogInteractiveComponent } from '../types/blog';

// ─── Vite glob imports ─────────────────────────────────────────────────────
// These resolve at build time — every .md file in content/blog/ is included.
const norwegianPosts = import.meta.glob('/content/blog/no/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const englishPosts = import.meta.glob('/content/blog/en/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// ─── Frontmatter parser ────────────────────────────────────────────────────
// Simple YAML frontmatter parser (no gray-matter dep at runtime — it's Node-only).
// Handles: strings, arrays (YAML list or JSON array), booleans, numbers, objects.

function parseFrontmatter(raw: string): { meta: Record<string, unknown>; content: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { meta: {}, content: raw };

  const yamlBlock = match[1];
  const content = match[2].trim();
  const meta: Record<string, unknown> = {};

  let currentKey = '';
  let collectingArray = false;
  let arrayItems: string[] = [];

  for (const line of yamlBlock.split('\n')) {
    // YAML list item (  - value)
    if (collectingArray && /^\s+-\s+/.test(line)) {
      arrayItems.push(line.replace(/^\s+-\s+/, '').replace(/^["']|["']$/g, '').trim());
      continue;
    }

    // End of array collection
    if (collectingArray) {
      meta[currentKey] = arrayItems;
      collectingArray = false;
      arrayItems = [];
    }

    // Key: value pair
    const kvMatch = line.match(/^(\w[\w-]*)\s*:\s*(.*)$/);
    if (kvMatch) {
      const [, key, rawValue] = kvMatch;
      const value = rawValue.trim();

      if (value === '') {
        // Might be start of a YAML list
        currentKey = key;
        collectingArray = true;
        arrayItems = [];
        continue;
      }

      // JSON array: [item1, item2]
      if (value.startsWith('[') && value.endsWith(']')) {
        try {
          meta[key] = JSON.parse(value);
        } catch {
          meta[key] = value.slice(1, -1).split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
        }
        continue;
      }

      // Boolean
      if (value === 'true') { meta[key] = true; continue; }
      if (value === 'false') { meta[key] = false; continue; }

      // Number
      if (/^\d+(\.\d+)?$/.test(value)) { meta[key] = parseFloat(value); continue; }

      // String (strip quotes)
      meta[key] = value.replace(/^["']|["']$/g, '');
    }
  }

  // Flush any remaining array
  if (collectingArray) {
    meta[currentKey] = arrayItems;
  }

  return { meta, content };
}

// ─── FAQ parser ────────────────────────────────────────────────────────────
// Parses FAQ from frontmatter (array of {question, answer} objects)
function parseFAQ(raw: unknown): BlogFAQ[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is BlogFAQ =>
      typeof item === 'object' && item !== null && 'question' in item && 'answer' in item,
  );
}

// ─── Interactive component parser ─────────────────────────────────────────
// Extracts interactiveComponent from raw frontmatter YAML block
function parseInteractiveComponent(yamlBlock: string): BlogInteractiveComponent | undefined {
  const sectionMatch = yamlBlock.match(/^interactiveComponent:\s*$/m);
  if (!sectionMatch) return undefined;

  const startIdx = sectionMatch.index! + sectionMatch[0].length;
  const lines = yamlBlock.slice(startIdx).split('\n');
  const fields: Record<string, string> = {};

  for (const line of lines) {
    // Stop at next top-level key (no leading whitespace)
    if (/^\S/.test(line) && line.trim() !== '') break;
    const kvMatch = line.match(/^\s+(\w[\w-]*)\s*:\s*(.+)$/);
    if (kvMatch) {
      fields[kvMatch[1]] = kvMatch[2].replace(/^["']|["']$/g, '').trim();
    }
  }

  if (!fields.type || !fields.title) return undefined;

  return {
    type: fields.type,
    title: fields.title,
    description: fields.description || '',
    trigger: fields.trigger,
    ageRange: fields.ageRange,
    outputFile: fields.outputFile,
  };
}

// ─── Reading time calculator ───────────────────────────────────────────────
function calculateReadingTime(content: string): number {
  const words = content.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200)); // 200 wpm average
}

// ─── Parse a single markdown file into BlogPost ────────────────────────────
function parsePost(_filePath: string, raw: string, lang: 'no' | 'en'): BlogPost | null {
  const { meta, content } = parseFrontmatter(raw);
  if (!meta.title || !meta.slug) return null;

  // Extract raw YAML block for nested object parsing
  const yamlMatch = raw.match(/^---\n([\s\S]*?)\n---/);
  const yamlBlock = yamlMatch ? yamlMatch[1] : '';

  const slug = String(meta.slug);
  const readingTime = meta.readingTime
    ? Number(meta.readingTime)
    : calculateReadingTime(content);

  return {
    slug,
    title: String(meta.title),
    description: String(meta.description || ''),
    author: String(meta.author || 'Good Atoms'),
    authorTitle: String(meta.authorTitle || ''),
    date: String(meta.date || ''),
    updatedAt: meta.updatedAt ? String(meta.updatedAt) : undefined,
    category: (meta.category as BlogCategory) || 'realfag',
    tags: Array.isArray(meta.tags) ? meta.tags.map(String) : [],
    primaryKeyword: String(meta.primaryKeyword || ''),
    secondaryKeywords: Array.isArray(meta.secondaryKeywords)
      ? meta.secondaryKeywords.map(String)
      : [],
    readingTime,
    lang,
    image: meta.image ? String(meta.image) : undefined,
    imageAlt: meta.imageAlt ? String(meta.imageAlt) : undefined,
    featured: meta.featured === true,
    faq: parseFAQ(meta.faq),
    translationSlug: meta.translationSlug ? String(meta.translationSlug) : undefined,
    interactiveComponent: parseInteractiveComponent(yamlBlock),
    content,
  };
}

// ─── Scheduled publishing gate ────────────────────────────────────────────
// Posts with a future date are hidden until that day arrives.
// This lets us push 10 articles at once and have them appear one per day.
function isPublished(post: BlogPost): boolean {
  if (!post.date) return true;
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  return post.date <= today;
}

// ─── Load all posts for a language ─────────────────────────────────────────
function loadPosts(
  files: Record<string, string>,
  lang: 'no' | 'en',
): BlogPost[] {
  return Object.entries(files)
    .map(([path, raw]) => parsePost(path, raw, lang))
    .filter((p): p is BlogPost => p !== null)
    .filter(isPublished) // hide future-dated posts
    .sort((a, b) => b.date.localeCompare(a.date)); // newest first
}

// ─── Cached post arrays (with daily cache bust for scheduled publishing) ──
let _noPosts: BlogPost[] | null = null;
let _enPosts: BlogPost[] | null = null;
let _cacheDate: string | null = null;

function bustCacheIfNewDay() {
  const today = new Date().toISOString().split('T')[0];
  if (_cacheDate !== today) {
    _noPosts = null;
    _enPosts = null;
    _cacheDate = today;
  }
}

export function getAllPosts(lang: 'no' | 'en'): BlogPost[] {
  bustCacheIfNewDay();
  if (lang === 'no') {
    if (!_noPosts) _noPosts = loadPosts(norwegianPosts, 'no');
    return _noPosts;
  }
  if (!_enPosts) _enPosts = loadPosts(englishPosts, 'en');
  return _enPosts;
}

export function getPostBySlug(slug: string, lang: 'no' | 'en'): BlogPost | undefined {
  return getAllPosts(lang).find(p => p.slug === slug);
}

export function getPostsByCategory(category: BlogCategory, lang: 'no' | 'en'): BlogPost[] {
  return getAllPosts(lang).filter(p => p.category === category);
}

export function getFeaturedPosts(lang: 'no' | 'en'): BlogPost[] {
  return getAllPosts(lang).filter(p => p.featured);
}

export function getRecentPosts(lang: 'no' | 'en', count = 6): BlogPost[] {
  return getAllPosts(lang).slice(0, count);
}

export function getRelatedPosts(post: BlogPost, count = 3): BlogPost[] {
  return getAllPosts(post.lang)
    .filter(p => p.slug !== post.slug && p.category === post.category)
    .slice(0, count);
}

// ─── Get all unique tags ───────────────────────────────────────────────────
export function getAllTags(lang: 'no' | 'en'): string[] {
  const tags = new Set<string>();
  getAllPosts(lang).forEach(p => p.tags.forEach(t => tags.add(t)));
  return Array.from(tags).sort();
}
